import { sanitizeAuthoredHtml } from '@low6-pickem/frontend-shared';
import React, { useMemo } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import GameCardFullWidth from '@/components/game/GameCardFullWidth';
import type { PickemDetailResponse } from '@low6-pickem/api-types';
import { getLeagueLogoUrl } from '@/utils/pickem-helpers';
import { getOrdinal } from '@/utils/ordinal';
import { useTranslation } from 'react-i18next';
import { usePickemRules } from '@/api/query/usePickem';
import { Skeleton } from '@/components/ui/skeleton';

// Shared prose styling so the rules content and the payout template render
// consistently (same type scale, heading weights, link colors, and table styling).
const PROSE_CLASS =
  'text-[12px] leading-5 text-white/90 [&_a]:text-[#53d337] [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-[#70e455] [&_h1]:text-[16px] [&_h1]:font-semibold [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-[15px] [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-[14px] [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_p]:mb-2 [&_table]:w-full [&_table]:border-collapse [&_table]:text-[11px] [&_th]:border [&_th]:border-white/15 [&_th]:bg-white/5 [&_th]:px-2 [&_th]:py-2 [&_th]:text-left [&_td]:border [&_td]:border-white/10 [&_td]:px-2 [&_td]:py-2';

interface ContestDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  pickemDetails?: PickemDetailResponse;
  competitionData?: any;
}

const ContestDetailsModal: React.FC<ContestDetailsModalProps> = ({
  isOpen,
  onClose,
  pickemDetails,
  competitionData,
}) => {
  const { t } = useTranslation();
  const pickemId = pickemDetails?.pickem.pickem_id ?? '';
  const {
    data: rulesData,
    isLoading: isRulesLoading,
    isError: isRulesError,
    refetch: refetchRules,
  } = usePickemRules(pickemId, isOpen && !!pickemId);
  const rules = rulesData?.rules;
  const payoutTemplate = rulesData?.payout_template;

  // Calculate total questions (excluding tiebreaker)
  const totalQuestions = useMemo(() => {
    return (
      pickemDetails?.pickem.questions.filter(q => q.question_type?.toLowerCase() !== 'tiebreaker')
        .length || 0
    );
  }, [pickemDetails]);

  // Map pickem data to game card props
  const gameCardData = useMemo(() => {
    if (!pickemDetails || !competitionData) {
      return null;
    }

    return {
      id: pickemDetails.pickem.pickem_id,
      title: competitionData.title || pickemDetails.pickem.title_en,
      prize: pickemDetails.pickem.prize_text_en || '$500',
      state: competitionData.state,
      gameDate: new Date(competitionData.gameDate),
      totalPicks: totalQuestions,
      leagueLogoUrl: getLeagueLogoUrl(pickemDetails.pickem.sport),
      totalEntries: competitionData.totalEntries ?? pickemDetails.pickem.entries ?? 0,
      entryCost: 'FREE', // TODO: Get from API if available
    };
  }, [pickemDetails, competitionData, totalQuestions]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="p-0 bg-app-background border-none w-full max-w-2xl top-0 translate-y-0 max-h-screen flex flex-col">
        {/* Header */}
        <div className="bg-app-background sticky top-0 z-50 px-4 py-4 flex items-center shadow-sm border-b border-border/30">
          <button
            onClick={onClose}
            className="p-2 hover:opacity-80 transition-opacity"
            aria-label="Go back"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20.25 12H3.75"
                stroke="#F5F5F5"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10.5 5.25L3.75 12L10.5 18.75"
                stroke="#F5F5F5"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <h1 className="flex-1 text-center text-text-primary text-xl font-bold mr-10">
            Contest Details
          </h1>
        </div>

        {/* Content */}
        <div className="overflow-y-auto pb-8 flex-1 min-h-0">
          {/* Game Card - Full Width */}
          {gameCardData && (
            <div className="mb-6">
              <GameCardFullWidth {...gameCardData} />
            </div>
          )}

          {/* Rules Section */}
          <div className="px-4 mb-6">
            {isRulesLoading ? (
              <div className="mt-4 space-y-3" aria-busy="true">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-11/12" />
                <Skeleton className="h-4 w-4/5" />
                <Skeleton className="h-5 w-32 mt-6" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ) : isRulesError ? (
              <div className="mt-4 flex flex-col items-start gap-3 rounded-md border border-white/10 bg-white/5 p-4">
                <p className="text-sm text-white/90">
                  We couldn't load the contest rules right now. Please try again.
                </p>
                <button
                  type="button"
                  onClick={() => refetchRules()}
                  className="rounded-md bg-[#53d337] px-3 py-1.5 text-sm font-semibold text-black transition-opacity hover:opacity-90"
                >
                  Retry
                </button>
              </div>
            ) : rules?.rules_text ? (
              <div
                className={`mt-4 ${PROSE_CLASS}`}
                dangerouslySetInnerHTML={{ __html: sanitizeAuthoredHtml(rules.rules_text) }}
              />
            ) : (
              <>
                <h2 className="text-white font-bold text-lg mb-3">How to Play:</h2>
                <ul className="text-white space-y-2 text-sm leading-relaxed">
                  <li className="flex">
                    <span className="mr-2">•</span>
                    <span>
                      After registration, entrants may make predictions ("Picks") for featured
                      games.
                    </span>
                  </li>
                  <li className="flex">
                    <span className="mr-2">•</span>
                    <span>
                      Picks must be submitted before the official start time of the first featured
                      game of each Daily Contest.
                    </span>
                  </li>
                  <li className="flex">
                    <span className="mr-2">•</span>
                    <span>
                      Entrants may modify Picks until the applicable contest deadline for which
                      Picks were made.
                    </span>
                  </li>
                  <li className="flex">
                    <span className="mr-2">•</span>
                    <span>Limit: One (1) entry per entrant per Daily Contest.</span>
                  </li>
                </ul>

                <h2 className="text-white font-bold text-lg mt-6 mb-3">Daily Prizes:</h2>
                <ul className="text-white space-y-2 text-sm leading-relaxed">
                  <li className="flex">
                    <span className="mr-2">•</span>
                    <span>
                      Each Daily Contest Winner is eligible to receive a share of $200 or more in
                      Delta Gift Cards.
                    </span>
                  </li>
                  <li className="flex">
                    <span className="mr-2">•</span>
                    <span>
                      Where Multiple Winners are possible, if multiple entrants achieve Perfect
                      Picks, then the total prizing pool will be shared among winners.
                    </span>
                  </li>
                </ul>

                <h2 className="text-white font-bold text-lg mt-6 mb-3">Monthly Prizes:</h2>
                <ul className="text-white space-y-2 text-sm leading-relaxed">
                  <li className="flex">
                    <span className="mr-2">•</span>
                    <span>1st Place: $1,500 Delta Gift Card</span>
                  </li>
                  <li className="flex">
                    <span className="mr-2">•</span>
                    <span>2nd Place: $1,000 Delta Gift Card</span>
                  </li>
                  <li className="flex">
                    <span className="mr-2">•</span>
                    <span>3rd Place: $500 Delta Gift Card</span>
                  </li>
                </ul>
                <p className="text-white text-sm mt-3 leading-relaxed">
                  Total Monthly Prize Pool: $3,000 Delta Gift Cards.
                </p>
              </>
            )}
          </div>

          {/* Payout Table */}
          {payoutTemplate && payoutTemplate.position_payouts.length > 0 && (
            <div className={`px-4 mb-6 ${PROSE_CLASS}`}>
              <h2>Prize Payouts:</h2>
              {(() => {
                const payouts = payoutTemplate.position_payouts;
                const isCashZero = (cash: string | null) => {
                  const n = Number(cash);
                  return cash === '' || cash === null || (!Number.isNaN(n) && n === 0);
                };
                const allCrownsZero = payouts.every(p => p.crowns === 0);
                const allCashZero = payouts.every(p => isCashZero(p.cash));
                // "Other" holds a free-text prize per rank (e.g. "$150 Delta Gift
                // Card") that Ops configures for non-cash contests like SkyPicks
                // (DKPLAT-628).
                const anyOther = payouts.some(p => p.other != null && p.other.trim() !== '');
                // Hide the Cash column when every rank is $0 (mirrors Crowns), but
                // keep it as a fallback if there's nothing else to show so the table
                // is never rank-only.
                const showCash = !allCashZero || (allCrownsZero && !anyOther);
                return (
                  <table>
                    <thead>
                      <tr>
                        <th>Rank</th>
                        {showCash && <th>Cash</th>}
                        {!allCrownsZero && <th>Crowns</th>}
                        {anyOther && <th>Prize</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {payouts.map(p => {
                        const cashNum = Number(p.cash);
                        const cashDisplay =
                          p.cash !== '' && p.cash !== null && !Number.isNaN(cashNum)
                            ? `$${p.cash}`
                            : p.cash;
                        // Ordinal rank labels (e.g. "1st", "3rd - 5th"). Range-mode
                        // rows cover a span; single-position rows have rank_to === rank.
                        const rankDisplay =
                          p.rank_to && p.rank_to !== p.rank
                            ? `${getOrdinal(p.rank, t)} - ${getOrdinal(p.rank_to, t)}`
                            : getOrdinal(p.rank, t);
                        return (
                          <tr key={p.rank}>
                            <td>{rankDisplay}</td>
                            {showCash && <td>{cashDisplay}</td>}
                            {!allCrownsZero && <td>{p.crowns}</td>}
                            {anyOther && <td>{p.other?.trim() || '—'}</td>}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContestDetailsModal;
